import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Badge } from './ui/badge'
import { toast } from 'react-hot-toast'
import { User, Lock, Shield, Key } from 'lucide-react'

const AccountSettings = ({ user, onUserUpdate }) => {
  const [profileData, setProfileData] = useState({
    full_name: user?.full_name || '',
    company_name: user?.company_name || '',
  })
  const [pwData, setPwData] = useState({ current_password: '', new_password: '', confirm: '' })
  const [savingProfile, setSavingProfile] = useState(false)
  const [savingPw, setSavingPw] = useState(false)

  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken')

  const handleProfileSave = async (e) => {
    e.preventDefault()
    setSavingProfile(true)
    try {
      const res = await fetch('/api/auth/me', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          full_name: profileData.full_name,
          company_name: profileData.company_name,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Fehler')
      toast.success('Profil gespeichert')
      onUserUpdate?.({ ...user, ...data })
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSavingProfile(false)
    }
  }

  const handlePasswordChange = async (e) => {
    e.preventDefault()
    if (pwData.new_password !== pwData.confirm) {
      toast.error('Passwörter stimmen nicht überein')
      return
    }
    if (pwData.new_password.length < 8) {
      toast.error('Mindestens 8 Zeichen')
      return
    }
    setSavingPw(true)
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          current_password: pwData.current_password,
          new_password: pwData.new_password,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Fehler')
      toast.success('Passwort geändert')
      setPwData({ current_password: '', new_password: '', confirm: '' })
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSavingPw(false)
    }
  }

  const tierBadge = {
    free: { label: 'Free', color: 'bg-slate-100 text-slate-700' },
    pro: { label: 'Pro', color: 'bg-blue-100 text-blue-700' },
    business: { label: 'Business', color: 'bg-purple-100 text-purple-700' },
    super_admin: { label: 'Super Admin', color: 'bg-red-100 text-red-700' },
  }[user?.subscription_tier || 'free'] || { label: 'Free', color: 'bg-slate-100 text-slate-700' }

  return (
    <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Account-Einstellungen</h1>
        <Badge className={`${tierBadge.color} px-3 py-1 rounded-full font-medium`}>{tierBadge.label}</Badge>
      </div>

      {/* Profile Info */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <User className="h-5 w-5 text-blue-600" />
            Profil
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileSave} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">E-Mail</label>
              <Input value={user?.email || ''} disabled className="bg-slate-50 text-slate-500" />
              <p className="text-xs text-slate-400 mt-1">E-Mail-Adresse kann nicht geändert werden.</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">Name</label>
              <Input
                value={profileData.full_name}
                onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                placeholder="Ihr vollständiger Name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">Firmenname</label>
              <Input
                value={profileData.company_name}
                onChange={(e) => setProfileData({ ...profileData, company_name: e.target.value })}
                placeholder="Ihr Firmenname"
              />
            </div>
            <Button type="submit" disabled={savingProfile} className="bg-blue-600 hover:bg-blue-700 text-white">
              {savingProfile ? 'Speichere...' : 'Profil speichern'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Lock className="h-5 w-5 text-blue-600" />
            Passwort ändern
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">Aktuelles Passwort</label>
              <Input
                type="password"
                value={pwData.current_password}
                onChange={(e) => setPwData({ ...pwData, current_password: e.target.value })}
                placeholder="••••••••"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">Neues Passwort</label>
              <Input
                type="password"
                value={pwData.new_password}
                onChange={(e) => setPwData({ ...pwData, new_password: e.target.value })}
                placeholder="Min. 8 Zeichen"
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">Neues Passwort bestätigen</label>
              <Input
                type="password"
                value={pwData.confirm}
                onChange={(e) => setPwData({ ...pwData, confirm: e.target.value })}
                placeholder="Passwort wiederholen"
                required
              />
            </div>
            <Button type="submit" disabled={savingPw} className="bg-blue-600 hover:bg-blue-700 text-white">
              {savingPw ? 'Speichere...' : 'Passwort ändern'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Subscription Info */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="h-5 w-5 text-blue-600" />
            Abonnement
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
            <div>
              <p className="font-semibold text-slate-800">
                {user?.subscription_tier === 'business' ? 'Business Plan' :
                 user?.subscription_tier === 'pro' ? 'Pro Plan' : 'Free Plan'}
              </p>
              <p className="text-sm text-slate-500 mt-0.5">
                {user?.subscription_tier === 'free'
                  ? 'Upgrade für API-Zugang und MCP-Integration'
                  : 'API-Zugang und MCP-Integration aktiv'}
              </p>
            </div>
            <Badge className={tierBadge.color + ' px-3 py-1 rounded-full'}>{tierBadge.label}</Badge>
          </div>
          {user?.subscription_tier === 'free' && (
            <Button className="mt-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:opacity-90 w-full">
              Auf Pro upgraden →
            </Button>
          )}
          {(user?.subscription_tier === 'pro' || user?.subscription_tier === 'business') && (
            <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
              <Key className="h-4 w-4" />
              <span>API-Keys verwalten: <button onClick={() => {}} className="text-blue-600 hover:underline">API-Keys öffnen</button></span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default AccountSettings
